import { logEvent } from "@src/appV2/lib/analytics/log";
import { type Dispatch, type SetStateAction, useCallback } from "react";

import { HEADER_HEIGHT, IMPERSONATION_USER_EVENTS, MIN_WINDOW_SIZE } from "../constants";
import type { ImpersonationSession } from "../types";

interface UseToggleMaximizeSessionOptions {
  employeeId: string | undefined;
  setSessions: Dispatch<SetStateAction<ImpersonationSession[]>>;
}

/**
 * Returns a stable `toggleMaximizeSession` callback that flips a single
 * impersonation session between maximized (filling the viewport) and restored
 * (back to the snapshot taken when it was maximized). Logs a Datadog event for
 * each toggle keyed by employee + session id.
 *
 * Lives in its own file purely to keep `useImpersonationSessionsHook` under
 * the appV2 ESLint `max-lines: 250` budget — it has no other reason to be
 * separate from the rest of the session-management surface.
 */
export function useToggleMaximizeSession({
  employeeId,
  setSessions,
}: UseToggleMaximizeSessionOptions) {
  return useCallback(
    (id: string) => {
      setSessions((previous) =>
        previous.map((session) => {
          if (session.id !== id) {
            return session;
          }

          if (session.isMaximized) {
            // Restore: prefer the snapshot we took at maximize time; fall back
            // to a sensible default centered window if it's missing.
            const restoreSize = session.restoreSize ?? {
              width: Math.max(MIN_WINDOW_SIZE.width, Math.round(window.innerWidth * 0.6)),
              height: Math.max(
                MIN_WINDOW_SIZE.height,
                Math.round((window.innerHeight - HEADER_HEIGHT) * 0.6)
              ),
            };
            const restorePosition = session.restorePosition ?? {
              x: Math.max(0, Math.round((window.innerWidth - restoreSize.width) / 2)),
              y: Math.max(
                0,
                Math.round((window.innerHeight - HEADER_HEIGHT - restoreSize.height) / 2)
              ),
            };
            return {
              ...session,
              size: restoreSize,
              position: restorePosition,
              isMaximized: false,
              restoreSize: undefined,
              restorePosition: undefined,
            };
          }

          // Maximize: snapshot current rect and fill the available viewport.
          return {
            ...session,
            restoreSize: session.size,
            restorePosition: session.position,
            isMaximized: true,
            size: {
              width: window.innerWidth,
              height: Math.max(MIN_WINDOW_SIZE.height, window.innerHeight - HEADER_HEIGHT),
            },
            position: { x: 0, y: 0 },
          };
        })
      );

      logEvent(IMPERSONATION_USER_EVENTS.SESSION_MAXIMIZE_TOGGLED, {
        employeeId,
        sessionId: id,
      });
    },
    [employeeId, setSessions]
  );
}
